import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { AxiosError } from 'axios'
import { MultiSelectDropdown } from 'components/common'
import { DateTime } from 'components/common/input/dateTime'
import toast from 'react-hot-toast'
import * as yup from 'yup'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { downloadNullEmpIdOrders } from 'utils/api/orderApi'
import { ORDER_STATUS } from 'utils/constants'
import ReportSubmitButton from './reportSubmitButton'

dayjs.extend(isSameOrAfter)

type NullEmpIdOrdersFormValues = {
  from?: string
  to?: string
  status?: string[]
}

const ORDER_STATUS_OPTIONS = Object.values(ORDER_STATUS).map((status) => ({
  label: status,
  value: status,
  disabled: false,
}))

// Every filter is optional - submitting an empty form exports all null empId
// orders, same as before the filters existed
const nullEmpIdOrdersSchema = yup.object({
  from: yup
    .string()
    .optional()
    .test('no-future', 'From date cannot be a future date', (value) => {
      if (!value) return true
      return !dayjs(value).isAfter(dayjs(), 'day')
    }),
  to: yup
    .string()
    .optional()
    .test('no-future', 'To date cannot be a future date', (value) => {
      if (!value) return true
      return !dayjs(value).isAfter(dayjs(), 'day')
    })
    .test('is-after-from', 'To must be after From', function (value) {
      const { from } = this.parent
      if (!from || !value) return true
      return dayjs(value).isSameOrAfter(dayjs(from))
    }),
  status: yup.array().of(yup.string().required()).optional(),
})

const NullEmpIdOrdersForm = () => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<NullEmpIdOrdersFormValues>({
    resolver: yupResolver(nullEmpIdOrdersSchema),
  })

  const handleFormSubmit = async (data: NullEmpIdOrdersFormValues) => {
    const toastId = toast.loading('Loading...', { duration: Infinity })

    try {
      // from/to go as plain YYYY-MM-DD - the API reads them as GST calendar days
      const filters: Record<string, string> = {}
      if (data.from) filters.from = data.from
      if (data.to) filters.to = data.to
      if (data.status?.length) filters.status = data.status.join(',')

      await downloadNullEmpIdOrders(filters)

      toast.success('File will be sent to you in email!', {
        id: toastId,
        duration: 3000,
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        const runningService = error?.response?.data?.message?.runningService
        if (runningService) {
          return toast.error(runningService, { id: toastId, duration: 3000 })
        }
      }
      toast.error('Failed to export Null EmpId Orders.', {
        id: toastId,
        duration: 3000,
      })
    }
  }

  return (
    <div>
      <h3 className="title">Null EmpId Orders</h3>

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="mb-5 flex flex-col gap-3"
      >
        <div className="flex gap-3">
          <DateTime
            label="From"
            name="from"
            register={register}
            max={dayjs().format('YYYY-MM-DD')}
            error={errors.from?.message}
          />
          <DateTime
            label="To"
            name="to"
            register={register}
            max={dayjs().format('YYYY-MM-DD')}
            error={errors.to?.message}
          />
        </div>

        <MultiSelectDropdown
          name="status"
          label="Status"
          setValue={setValue}
          includeSelectAll
          options={ORDER_STATUS_OPTIONS}
        />

        <ReportSubmitButton buttonText="Export" loading={isSubmitting} />
      </form>
    </div>
  )
}

export default NullEmpIdOrdersForm
